import { createSlice, createSelector } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { Asset, BridgeModel, BridgeType, CustomTemplate, Folder, Task } from "~/types/bridge";
import type { DefectType, ElementGroup, ElementType, Extent, Severity, StructureType } from "~/types/defects";
import { selectImagesFolderTree, selectModels, selectSurveyFiles } from "./displayedSurveySlice";
import { addFilesToTree, createTree } from "~/utils/folderTreeUtils";
import { selectElements } from "./assetSlice";
import type { NumericSystem } from "~/types/cesium";
import type { Report, TemplateReport } from "~/types/reports";
import { fetchOrganization, fetchOrganizationDataType, fetchOrganizationInfo, fetchOrganizationTechInfo, fetchProviderInfo } from "~/thunks/organizationThunks";

import { transformAsset } from "~/utils/assetUtils";

import { createAssetThunk, createProviderThunk, createSurveysThunk, createUserThunk } from "~/thunks/createThunks";
import type { RootState } from "~/store";
import type { Organization, Survey } from "~/types/general";
import type { CloudImage } from "~/types/CloudImage";
import { uploadImageThunk } from "~/thunks/uploadThunk";
import type { User } from "~/types/userManagement";
import { updateSingleThunk } from "~/thunks/updateThunks";



interface OrganizationState {
  organization: Organization | null;
  provider: any | null;
  users: User[];
  roles: any[];
  providers: any[];
  clients: any[];
  assets: Asset[];
  surveys: any[];
  tasks: Task[];
  // processTemplates: any[];
  processTasks: any[];
  workOrders: any[];
  bridgeTypes: BridgeType[];
  elementsTypes: ElementType[];
  elementGroups: ElementGroup[];
  structureTypes: StructureType[];
  templateReports: TemplateReport[];
  customTemplates: CustomTemplate[] | null;
  folderStructure: Folder[];
  defectTypes: DefectType[];
  numericSystem: NumericSystem,
  severities: Severity[];
  extents: Extent[];
  reports: Report[];
  loading: boolean;
  error: string | null;
}

const initialState: OrganizationState = {
  organization: null,
  provider: null,
  users: [],
  roles: [],
  providers: [],
  clients: [],
  surveys: [],
  assets: [],
  tasks: [],
  // processTemplates: [],
  processTasks: [],
  bridgeTypes: [],
  templateReports: [],
  workOrders: [],
  customTemplates: null,
  elementsTypes: [],
  elementGroups: [],
  structureTypes: [],
  folderStructure: [],
  defectTypes: [],
  numericSystem: "meter",
  severities: [],
  extents: [],
  reports: [],
  loading: false,
  error: null,
};
const organizationSlice = createSlice({
  name: "organization",
  initialState,
  reducers: {
    setNumericSystem(state, action: PayloadAction<NumericSystem>) {
      state.numericSystem = action.payload;
    },
    addRole(state, action: PayloadAction<{ newRole: any }>) {
      const { newRole } = action.payload;
      state.roles.push(newRole);  
    },
    updateRole(state, action: PayloadAction<{ updatedRole: any }>) {
      const { updatedRole } = action.payload;
      const roleIndex = state.roles.findIndex(role => role.id === updatedRole.id);
      if (roleIndex !== -1) {
        state.roles[roleIndex] = { ...state.roles[roleIndex], ...updatedRole };
      }
    },
    addProvider(state, action: PayloadAction<{ newProvider: any }>) {
      const { newProvider } = action.payload;
      state.providers.push(newProvider);  
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganization.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrganization.fulfilled, (state, action: PayloadAction<any>) => {
        console.log("fetchOrganization fulfilled", action.payload);
        state.loading = false;
        state.organization = action.payload.organization;
        state.numericSystem = action.payload.organization.unit_system || "Meter";
        state.assets = action.payload.assets.map((asset: any) => transformAsset(asset));
        const orgReports = JSON.parse(action.payload.organization.reports_template);
        state.reports = orgReports;

        if (action.payload.provider)
          state.provider = action.payload.provider;
        
      })
      .addCase(createAssetThunk.fulfilled, (state, action: PayloadAction<any>) => {
        console.log("createAssetThunk fulfilled", action.payload);
        state.loading = false;
        state.assets.push(transformAsset(action.payload));
      })
      .addCase(fetchOrganizationDataType.fulfilled, (state, action: PayloadAction<any>) => {
        console.log("fetchOrganizationDataType fulfilled", action.payload);
        if (action.payload.dataType === "assets") {

          const fixedAssets = action.payload.data.map((asset: any) => transformAsset(asset));
          console.log("fetchOrganizationDataType fulfilled", fixedAssets);
          state.loading = false;
          state.assets = fixedAssets;
        } else if (action.payload.dataType === "surveys") {
          state.surveys = action.payload.surveys;
        }
      })
      .addCase(fetchOrganization.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchOrganizationInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSingleThunk.fulfilled, (state, action: PayloadAction<{
        dataType: string,
        data: any
      }>) => {

        if (action.payload.dataType == 'assets') {
          
          const asset = state.assets.find(asset => asset.bid === action.payload.data.bid);
          if (asset) {
            let updatedAsset = {
            ...asset,
            ...action.payload.data,
            };
            const updatedAssets = state.assets.map(asset => asset.bid === updatedAsset.bid ? updatedAsset : asset);
            state.assets = updatedAssets;
          }
        }
        if (action.payload.dataType == 'organizations') {
          if (state.organization && state.organization.id === action.payload.data.id) {
            state.organization = {
              ...state.organization,
              ...action.payload.data,
            };
          }
          if (state.provider && state.provider.id === action.payload.data.id) {
            state.provider = {
              ...state.provider,
              ...action.payload.data,
            };
          }
        }


      })
      .addCase(createProviderThunk.fulfilled, (state, action: PayloadAction<any>) => {
        console.log("createProviderThunk fulfilled", action.payload);
        // console.log(state.users)
        state.loading = false;
        if (action.payload.provider) {
          state.providers.push(action.payload.provider);
        }
        if (action.payload.user) {
          const newOrgUser = {
            ...action.payload.user,
            providerName: action.payload.provider ? action.payload.provider.name : null,
            name: action.payload.user.first_name + " " + action.payload.user.last_name,
          }
          state.users.push(newOrgUser);
        }
      })
      .addCase(createUserThunk.fulfilled, (state, action: PayloadAction<any>) => {
        // console.log("createUserThunk fulfilled", action.payload);
        state.loading = false;
        if (action.payload.user) {
          const newOrgUser = {
            ...action.payload.user,
            id: action.payload.userConnection.id,
            user_id: action.payload.user.id,
            providerName: action.payload.provider ? action.payload.provider.name : null,
            name: action.payload.user.first_name + " " + action.payload.user.last_name,
            role_id: action.payload.userConnection.role_id,
            
          }
          console.log('adding newOrgUser', newOrgUser);
          state.users.push(newOrgUser);
        }
      })
      .addCase(fetchOrganizationInfo.fulfilled, (state, action: PayloadAction<any>) => {
        console.log("fetchOrganizationInfo fulfilled", action.payload);
        state.loading = false;
        const organizationusers = action.payload.orgUsers.map((user: any) => ({
          ...user,
          providerName: user.provider_id ? action.payload.providers.find((p: any) => p.id === user.provider_id)?.name : null,
        }));
        state.organization = { ...state.organization, ...action.payload.organization };
        state.users = organizationusers;
        state.roles = action.payload.orgRoles;
        state.providers = action.payload.providers;
        state.surveys = action.payload.surveys;
        state.tasks = action.payload.tasks;
        state.workOrders = action.payload.workOrders;
        state.templateReports = action.payload.templateReports;
        state.processTasks = action.payload.processTasks;
        state.customTemplates = action.payload.customTemplates;
      })
      .addCase(fetchProviderInfo.fulfilled, (state, action: PayloadAction<any>) => {
        console.log("fetchProviderInfo fulfilled", action.payload);
        state.loading = false;
        state.users = action.payload.users;
        state.roles = action.payload.roles;
        state.clients = action.payload.clients;
        state.surveys = action.payload.surveys;
        state.tasks = action.payload.tasks;
      })
      .addCase(fetchOrganizationInfo.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchOrganizationTechInfo.fulfilled, (state, action: PayloadAction<any>) => {
        console.log("fetchOrganizationTechInfo fulfilled", action.payload);
        state.loading = false;
        state.bridgeTypes = action.payload.bridgeTypes;
        state.elementsTypes = action.payload.elementsTypes
        state.elementGroups = action.payload.elementsGroups;
        state.structureTypes = action.payload.structureTypes;
        state.defectTypes = action.payload.defectTypes;
        state.folderStructure = action.payload.folderStructure;
        state.severities = action.payload.severities;
        state.extents = action.payload.extent;

      })
      .addCase(fetchOrganizationTechInfo.rejected, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(uploadImageThunk.fulfilled, (state, action: PayloadAction<any>) => {
        console.log("uploadImageThunk fulfilled", action.payload);
        if (action.payload.dataType === 'organizations' && action.payload.data.id) {
          // Update organization profile image
          if (state.organization && state.organization.id === action.payload.data.id) {
            state.organization = {
              ...state.organization,
              profile_image: action.payload.data.profile_image,
            };
          }
          if (state.provider && state.provider.id === action.payload.data.id) {
            state.provider = {
              ...state.provider,
              profile_image: action.payload.data.profile_image,
            };
          }
        }
        if (action.payload.dataType === 'assets' && action.payload.data.bid) {
          // Update asset image
          const assetIndex = state.assets.findIndex((asset) => asset.bid === action.payload.data.bid);
          if (assetIndex !== -1) {
            state.assets[assetIndex] = {
              ...state.assets[assetIndex],
              image_url: action.payload.data.image_url,
            };
          }

        }
      })
      .addCase(createSurveysThunk.fulfilled, (state, action: PayloadAction<any>) => {
        console.log("createSurveysThunk fulfilled", action.payload);
        let newSurveys: Survey[] = [];
        let newTasks: Task[] = [];
        action.payload.forEach((surveyWithTasks: any) => {
          newSurveys.push(surveyWithTasks.survey);
          newTasks = [...newTasks, ...surveyWithTasks.tasks];
        });
        state.surveys = [...state.surveys, ...newSurveys];
        state.tasks = [...state.tasks, ...newTasks];
        // state.surveys = [...state.surveys, ...action.payload.surveys];
      });
  },
});
export const { setNumericSystem, updateRole, addRole } = organizationSlice.actions;
export default organizationSlice.reducer;
export const selectOrganization = (state: RootState) => state.organization.organization;
export const selectProvider = (state: RootState) => state.organization.provider;
export const selectOrganizationUsers = (state: RootState) => state.organization.users;
export const selectOrganizationRoles = (state: RootState) => state.organization.roles;
export const selectOrganizationProviders = (state: RootState) => state.organization.providers;
export const selectOrganizationClients = (state: RootState) => state.organization.clients;
export const selectOrgReports = (state: RootState) => state.organization.reports;
export const selectBridgeTypes = (state: RootState) => state.organization.bridgeTypes;
export const selectElementsTypes = (state: RootState) => state.organization.elementsTypes;
export const selectElementGroups = (state: RootState) => state.organization.elementGroups;
export const selectStructureTypes = (state: RootState) => state.organization.structureTypes;
export const selectDefectTypes = (state: RootState) => state.organization.defectTypes;
export const selectAssets = (state: RootState) => state.organization.assets;
export const selectOrganizationFolderStructure = (state: RootState) => state.organization.folderStructure;
export const selectSeverities = (state: RootState) => state.organization.severities;
export const selectTemplateReports = (state: RootState) => state.organization.templateReports;
export const selectExtents = (state: RootState) => state.organization.extents;
export const selectWorkOrders = (state: RootState) => state.organization.workOrders;
export const selectNumericSystem = (state: RootState) => state.organization.numericSystem;
export const selectOrgSurveys = (state: RootState) => state.organization.surveys;
export const selectOrgTasks = (state: RootState) => state.organization.tasks;
export const selectCustomTemplates = (state: RootState) => state.organization.customTemplates;
export const selectProcessTasks = (state: RootState) => state.organization.processTasks;
export const selectOrganizationLoading = (state: RootState) => state.organization.loading;
export const selectProcessTemplates = (state: RootState) => {
  const templatesMap: { [key: string]: any } = {};
  state.organization.processTasks.forEach((task: any) => {
    if (!templatesMap[task.name]) {
      templatesMap[task.name] = {
        id: task.name,
        name: task.display_name,
      };
    }
  });
  return Object.values(templatesMap);
};

export const selectIsIsraeliOrganization = createSelector(
  selectOrganization,
  (organization) => {
    const israeliOrganizationIds = [201, 220];
    return organization && organization.id ? israeliOrganizationIds.includes(organization.id) : false;
  }
);

export const makeSelectOrganizationFolderTree = createSelector(
  selectOrganizationFolderStructure, selectImagesFolderTree, selectSurveyFiles, selectModels,
  (folderStructure: any[], folderTree: any[], surveyFiles: any, models: BridgeModel[]) => {
    console.log("makeSelectOrganizationFolderTree", { folderStructure, folderTree, surveyFiles, models });
    if (!folderStructure || folderStructure.length === 0) {
      return []
    }
    const folderStructurePaths =
      folderStructure &&
      folderStructure.map(folder => folder.path);
    let tree = createTree(folderStructurePaths);

    if (!surveyFiles || !surveyFiles.general) {
      return tree;
    }
    const filledFolderTree = addFilesToTree(tree, surveyFiles.general);
    filledFolderTree[0].children = folderTree;
    return filledFolderTree;
  }
  ,
)
export const makeSelectElementTypes = createSelector(
  selectElementsTypes, selectElements, (elementsTypes, elements) => {
    return elementsTypes
      .map((type: ElementType) => {
        const updatedType = { ...type };
        const elementsByType = elements.filter(
          (element: any) => element.element_type_id == type.id,
        );
        let sumPrimaryQuantity = 0;
        elementsByType.forEach((el: any) => {
          if (el.primary_quantity) sumPrimaryQuantity += el.primary_quantity;
        });
        let sumSecondaryQuantity = 0;
        elementsByType.forEach((el: any) => {
          if (el.secondary_quantity)
            sumSecondaryQuantity += el.secondary_quantity;
        });

        // updatedType.element_group = state.elementsGroups.find(group => group.id == type.element_group_id).name
        updatedType.elements = elementsByType;
        updatedType.total_elements = elementsByType.length;
        updatedType.primary_quantity = sumPrimaryQuantity;
        updatedType.secondary_quantity = sumSecondaryQuantity;
        return updatedType;
      });
  }
)
export const makeElementTypesWithElements = createSelector(makeSelectElementTypes, elementsTypes =>
  elementsTypes.filter((type: ElementType) => type.total_elements && type.total_elements > 0),
);




